/**
 * Wraps every occurrence of the active search term inside `text` in a `<mark>`,
 * so a field's cell can show exactly which part of its content the row was
 * matched on -- not just that it matched, which is all the cell's own
 * `matches-search` background tint conveys. A render function rather than a
 * template because the match count varies per value, so this needs to build a
 * variable list of text/mark nodes rather than fill in a fixed template shape.
 *
 * Deliberately built as plain text nodes plus real `<mark>` VNodes -- not a
 * `v-html` string concatenation -- so a cell value containing `<`, `>` or `&`
 * can never be interpreted as markup. Every field type that displays a plain,
 * already-formatted string can drop this in as a one-line replacement for
 * `{{ value }}`; it is a no-op (renders the same as `{{ text }}`) whenever
 * there is no search term or no match, so it is always safe to use.
 */
import { h } from 'vue'
import { escapeRegExp } from '@baserow/modules/core/utils/string'

// Mirrors `MIN_TRIGRAM_SEARCH_TERM_LENGTH` in `database/utils/view.js`, which
// itself mirrors the backend's own trigram fallback threshold. Below this
// length neither side attempts a substring match, so marking anything shorter
// here would highlight text the row was never actually matched on.
const MIN_SEARCH_TERM_LENGTH = 3

export default {
  name: 'SearchHighlightedText',
  props: {
    text: {
      type: [String, Number],
      default: '',
    },
    searchTerm: {
      type: String,
      required: false,
      default: '',
    },
  },
  render() {
    const text =
      this.text === null || this.text === undefined ? '' : `${this.text}`
    const term = this.searchTerm.trim()

    // `null`, not `''`. A render function returning an empty string produces
    // NOTHING on the server and an empty Text node on the client, so every
    // blank cell hydrated as "Server rendered element contains fewer child
    // nodes than client vdom" -- 149 of them on a single grid, measured with
    // `__VUE_PROD_HYDRATION_MISMATCH_DETAILS__`. Returning `null` renders a
    // comment anchor on both sides instead, which hydrates cleanly.
    //
    // This is why the warning tracked how much *blank data* a table held
    // rather than its field types or row count: tables whose cells were all
    // filled never tripped it.
    if (text === '') {
      return null
    }

    if (term.length < MIN_SEARCH_TERM_LENGTH) {
      return text
    }

    const regex = new RegExp(escapeRegExp(term), 'gui')
    const nodes = []
    let lastIndex = 0
    let match = regex.exec(text)

    if (match === null) {
      return text
    }

    while (match !== null) {
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index))
      }
      nodes.push(h('mark', { class: 'search-highlighted-text' }, match[0]))
      lastIndex = match.index + match[0].length

      // A zero-length match can't happen with a non-empty escaped term, but
      // guard the advance anyway so a pathological input can never spin this
      // into an infinite loop.
      if (regex.lastIndex === match.index) {
        regex.lastIndex += 1
      }
      match = regex.exec(text)
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex))
    }

    return nodes
  },
}
